/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';
import type { CSSProperties } from 'react';

import TooltipWrapper from 'bundles/common/components/Tooltip/TooltipWrapper';
import { CheckIcon, XIcon } from 'bundles/course-frame/components/course-outline/icons';

import _t from 'i18n!nls/course-frame';

/** Extends CSSProperties to include only our specific CSS custom properties */
type CSSPropertiesWithVars = CSSProperties & {
  [K in (typeof CSS_VARS)[keyof typeof CSS_VARS]]?: string;
};

/** Progress icon states */
export type ProgressIconItemState = 'complete' | 'incomplete' | 'failed';

/**
 * Shared hover styles for navigation items with ProgressIcon.
 * Apply these styles to parent containers that wrap ProgressIcon to get consistent hover behavior.
 *
 * @example
 * ```tsx
 * const styles = css`
 *   display: flex;
 *   padding: 12px;
 *   ${progressIconHoverStyles}
 * `;
 * ```
 */
export const progressIconHoverStyles = css`
  &:hover {
    text-decoration: none;

    /* Override ProgressIcon CSS variables on hover */
    & .rc-cf-progress-icon {
      --cf-progress-icon-selected-color: var(--cds-color-purple-950);
      --cf-progress-icon-not-selected-color: var(--cds-color-neutral-stroke-primary);
      --cf-progress-icon-gradient: linear-gradient(
        122deg,
        var(--cds-color-purple-400) -6.25%,
        var(--cds-color-purple-950) 37.7%
      );
    }
  }
`;

/** Maps progress icon states to translated labels for accessibility */
const getStateLabel = (itemState: ProgressIconItemState): string => {
  switch (itemState) {
    case 'complete':
      return _t('Complete');
    case 'incomplete':
      return _t('Not complete');
    case 'failed':
      return _t('Failed');
    default:
      return '';
  }
};

const CIRCLE_SIZE = 16;
const CIRCLE_BORDER_WIDTH = 2;
const CONNECTING_LINE_WIDTH_INACTIVE = 1;
const CONNECTING_LINE_WIDTH_ACTIVE = 2;
const ICON_SIZE = 12;

// CSS variable names as constants
const CSS_VARS = {
  // intended to be overridden by the parent component
  SELECTED_COLOR: '--cf-progress-icon-selected-color',
  NOT_SELECTED_COLOR: '--cf-progress-icon-not-selected-color',
  GRADIENT: '--cf-progress-icon-gradient',
  // Dynamic variables for performance optimization
  LINE_WIDTH: '--cf-progress-icon-line-width',
  LINE_COLOR: '--cf-progress-icon-line-color',
  LINE_LEFT: '--cf-progress-icon-line-left',
  LINE_HEIGHT: '--cf-progress-icon-line-height',
  CIRCLE_BORDER_COLOR: '--cf-progress-icon-circle-border-color',
} as const;

// Common style patterns
const FLEX_CENTER = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** Calculates dynamic CSS variables for performance optimization */
const getDynamicStyleVars = (containerPaddingY: number = 0): CSSPropertiesWithVars => {
  const lineHeight = `calc(50% - ${CIRCLE_SIZE / 2}px + ${containerPaddingY}px)`;

  return {
    [CSS_VARS.LINE_HEIGHT]: lineHeight,
  };
};

type Props = {
  /** Current progress state: 'complete' (check icon), 'incomplete' (empty circle), 'failed' (X icon) */
  itemState: ProgressIconItemState;

  /** Whether item is selected/active. Controls line thickness and colors. @default false */
  isSelected?: boolean;

  /** Optional tooltip message displayed on hover */
  tooltipMessage?: string;

  /** Specify parent container padding in px for line (ascender/descender) height calculations.
   * This component will automatically assume the vertical space available to it but,
   * if the parent component has Y-padding around ProgressIcon, the vertical lines may not
   * extend far enough for your use case.
   * You can use this prop so that the lines can extend beyond the parent container's Y-padding.
   * @default 0 */
  containerPaddingY?: number;

  /** Test identifier. @default 'progress-icon' */
  'data-testid'?: string;
};

const styles = {
  container: css`
    width: ${CIRCLE_SIZE}px;
    position: relative;
    align-self: stretch;

    /* Override these CSS variables to implement custom styles for hover.
    It will change ascender, descender, and circle colors */
    ${CSS_VARS.NOT_SELECTED_COLOR}: var(--cds-color-neutral-disabled-strong);
    ${CSS_VARS.SELECTED_COLOR}: var(--cds-color-emphasis-quaternary-stroke-default);
    ${CSS_VARS.GRADIENT}: linear-gradient(
      160deg,
      var(--cds-color-purple-400) 1.28%,
      var(--cds-color-purple-900) 80.59%
    );
  `,

  connectingLinesContainer: css`
    width: ${CIRCLE_SIZE}px;
    height: 100%;
    position: relative;
    z-index: 1;

    ${FLEX_CENTER}
    ${CSS_VARS.LINE_WIDTH}: ${CONNECTING_LINE_WIDTH_INACTIVE}px;
    ${CSS_VARS.LINE_COLOR}: var(${CSS_VARS.NOT_SELECTED_COLOR});
    ${CSS_VARS.LINE_LEFT}: calc(50% - ${CONNECTING_LINE_WIDTH_INACTIVE / 2}px);
    ${CSS_VARS.CIRCLE_BORDER_COLOR}: var(${CSS_VARS.NOT_SELECTED_COLOR});
  `,

  withAscender: css`
    &::before {
      content: '';
      position: absolute;
      width: var(${CSS_VARS.LINE_WIDTH});
      height: var(${CSS_VARS.LINE_HEIGHT});
      background-color: var(${CSS_VARS.LINE_COLOR});
      left: var(${CSS_VARS.LINE_LEFT});
      bottom: calc(50% + ${CIRCLE_SIZE / 2}px);
      pointer-events: none;
      border-radius: 50% 50% 0 0 / 1px 1px 0 0; /* rounded top, flat bottom */
    }
  `,

  withDescender: css`
    &::after {
      content: '';
      position: absolute;
      width: var(${CSS_VARS.LINE_WIDTH});
      height: var(${CSS_VARS.LINE_HEIGHT});
      background-color: var(${CSS_VARS.LINE_COLOR});
      left: var(${CSS_VARS.LINE_LEFT});
      top: calc(50% + ${CIRCLE_SIZE / 2}px);
      pointer-events: none;
      border-radius: 0 0 50% 50% / 0 0 1px 1px; /* flat top, rounded bottom */
    }
  `,

  circle: css`
    width: ${CIRCLE_SIZE}px;
    height: ${CIRCLE_SIZE}px;
    border-radius: 50%;
    position: relative;
    ${FLEX_CENTER}
    border: ${CIRCLE_BORDER_WIDTH}px solid;
    border-color: var(${CSS_VARS.CIRCLE_BORDER_COLOR});
  `,

  circleComplete: css`
    border: none;
    background: var(${CSS_VARS.GRADIENT});
  `,

  connectingLinesContainerSelected: css`
    z-index: 2;
    ${CSS_VARS.LINE_WIDTH}: ${CONNECTING_LINE_WIDTH_ACTIVE}px;
    ${CSS_VARS.LINE_COLOR}: var(${CSS_VARS.SELECTED_COLOR});
    ${CSS_VARS.LINE_LEFT}: calc(50% - ${CONNECTING_LINE_WIDTH_ACTIVE / 2}px);
    ${CSS_VARS.CIRCLE_BORDER_COLOR}: var(${CSS_VARS.SELECTED_COLOR});
  `,
};

/**
 * Progress indicator with connecting lines for course outlines.
 * Shows completion states and selection highlighting.
 *
 * The connecting lines (ascender/descender) are always rendered and controlled
 * by CSS selectors from parent components (e.g., CourseModule).
 *
 * @example
 * ```tsx
 * <ProgressIcon
 *   itemState="complete"
 *   isSelected={true}
 *   tooltipMessage="Lesson completed"
 *   containerPaddingY={8}
 * />
 * ```
 */
const ProgressIcon = ({
  itemState,
  isSelected = false,
  tooltipMessage,
  containerPaddingY = 0,
  'data-testid': dataTestId = 'progress-icon',
}: Props) => {
  const ariaLabel = useMemo(() => {
    const stateLabel = getStateLabel(itemState);
    const selectionLabel = isSelected ? _t('selected') : _t('unselected');

    return [stateLabel, selectionLabel].filter(Boolean).join(', ');
  }, [itemState, isSelected]);

  const circleContent = useMemo(() => {
    if (itemState === 'complete') {
      return <CheckIcon size={ICON_SIZE} />;
    }
    if (itemState === 'failed') {
      return <XIcon size={ICON_SIZE} />;
    }
    return null;
  }, [itemState]);

  // Get dynamic CSS variable values for performance
  const dynamicStyleVars = useMemo(() => getDynamicStyleVars(containerPaddingY), [containerPaddingY]);

  const circleStyles = useMemo(() => {
    const isCompleteOrFailed = itemState === 'complete' || itemState === 'failed';
    return isCompleteOrFailed ? [styles.circle, styles.circleComplete] : [styles.circle];
  }, [itemState]);

  const connectingLinesStyles = useMemo(() => {
    return [
      styles.connectingLinesContainer,
      styles.withAscender,
      styles.withDescender,
      ...(isSelected ? [styles.connectingLinesContainerSelected] : []),
    ];
  }, [isSelected]);

  const circleElement = (
    <div className="rc-cf-progress-icon-lines" css={[connectingLinesStyles]} style={dynamicStyleVars}>
      <div css={circleStyles}>{circleContent}</div>
    </div>
  );

  const circleWithTooltip = tooltipMessage ? (
    <TooltipWrapper message={tooltipMessage} placement="right">
      {circleElement}
    </TooltipWrapper>
  ) : (
    circleElement
  );

  return (
    <div
      className="rc-cf-progress-icon"
      css={styles.container}
      role="img"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {circleWithTooltip}
    </div>
  );
};

export default ProgressIcon;
